// src/components/home/Home.js
import React, { useEffect, useState, useMemo } from 'react';
import { Video, MessageCircle, CheckCircle, Moon, Utensils, X } from 'lucide-react';
import StatsCard from './StatsCard';
import UpdateCard from './UpdateCard';
import AnnouncementBanner from './AnnouncementBanner';

import { useAtom } from 'jotai';
import { activeChildDetail, appConstants, ChildTimeLineAtom } from '../../atomstore/store';
import { useRequestProcessor } from '../../requestProcessor';
import { fetchChildTimeline, fetchAttendanceConcerns, raiseAttendanceConcern, getConfirmYourSeat } from '../../queryfunctions/queryfunctions';
import TrialAdmissionBanner from './TrialAdmissionBanner';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import SpinnerLoading from '../common/SpinnerLoading';
import { findTimelineGroupForDate } from './childSchoolPresence';
import { getIndiaCalendarToday, toIndiaCalendarDate } from './transformTimeline';
import { isEnabledByDefault } from '../../utils/configFlag';

export const SERVER_DATE_FORMAT = "YYYY-MM-DD";

const textOrDefault = (value, fallback) => {
    if (typeof value !== 'string') return fallback;
    const trimmed = value.trim();
    return trimmed || fallback;
};

const normalizeConcernTime = (value) => {
    if (value == null || value === '') return '';
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (/^\d{2}:\d{2}:\d{2}/.test(trimmed)) {
            return trimmed.slice(0, 8);
        }
    }
    const asTime = moment(value, ['HH:mm:ss', 'hh:mm a', 'HH:mm'], true);
    if (asTime.isValid()) return asTime.format('HH:mm:ss');
    const parsed = moment(value);
    return parsed.isValid() ? parsed.format('HH:mm:ss') : '';
};

const getUpdateConcernTime = (update) => {
    if (update?.momentTime?.isValid?.()) {
        return update.momentTime.format('HH:mm:ss');
    }
    return normalizeConcernTime(update?.time);
};

const Home = ({ setSelectedPhoto }) => {
    const [showMoreUpdates, setShowMoreUpdates] = useState(false);
    const [activeChild] = useAtom(activeChildDetail);
    const [ChildTimeline, setTimeline] = useAtom(ChildTimeLineAtom);
    const [childTimeline, setChildTimeline] = useState({ startIndex: 0 });
    const [constants] = useAtom(appConstants);
    const [, setDataAvailable] = useState(true);
    const [, setLength] = useState(0);
    const [isAbsent, setIsAbsent] = useState(false);
    const [concernUpdate, setConcernUpdate] = useState(null);
    const [concernText, setConcernText] = useState('');
    const [concernError, setConcernError] = useState('');
    const [submittedConcernTimes, setSubmittedConcernTimes] = useState([]);
    const navigate = useNavigate()
    const [stats, setStats] = useState({
        checkin: '',
        naps: '',
        meals: '',
    })
    const [selectedDate, setSelectedDate] = useState(
        getIndiaCalendarToday()
    );

    const { useQueryWrapper, useMutationWrapper } = useRequestProcessor();

    const { data: trialSeat } = useQueryWrapper(
        ['confirmYourSeat', activeChild?.id],
        () => getConfirmYourSeat(activeChild),
        {
            enabled: !!activeChild?.id,
            retry: 0,
            refetchOnWindowFocus: false,
        }
    );

    const trialBanner = trialSeat?.message && trialSeat?.payment_link ? (
        <TrialAdmissionBanner
            message={trialSeat.message}
            buttonTitle={trialSeat.payment_button_title}
            paymentLink={trialSeat.payment_link}
        />
    ) : null;

    const ITEM_PER_PAGE = 50;

    const showRaiseConcern = isEnabledByDefault(constants?.showRaiseConcern);
    const { data: todayConcerns = [], refetch: refetchTodayConcerns } = useQueryWrapper(
        ['attendanceConcerns', activeChild?.id, selectedDate],
        () => fetchAttendanceConcerns(selectedDate),
        { enabled: showRaiseConcern && !!activeChild?.id && !!selectedDate }
    );

    const todayConcernTimes = useMemo(() => {
        const times = new Set(submittedConcernTimes);
        (todayConcerns || []).forEach((concern) => {
            const time = normalizeConcernTime(concern?.attendance_time);
            if (time) times.add(time);
        });
        return times;
    }, [todayConcerns, submittedConcernTimes]);

    useEffect(() => {
        setSubmittedConcernTimes([]);
    }, [activeChild?.id, selectedDate]);

    const openConcernDialog = (update) => {
        setConcernUpdate(update);
        setConcernText('');
        setConcernError('');
    };

    const closeConcernDialog = () => {
        setConcernUpdate(null);
        setConcernText('');
        setConcernError('');
    };

    const submitAttendanceConcern = useMutationWrapper(
        ['raiseAttendanceConcern'],
        (payload) => raiseAttendanceConcern(payload),
        {
            onSuccess: (data, variables) => {
                const shouldHideButton =
                    data?.status === 'ok' ||
                    (data?.error && /already been raised/i.test(data.error));
                if (!shouldHideButton) return;

                const time = normalizeConcernTime(variables?.attendance_time);
                if (time) {
                    setSubmittedConcernTimes((prev) =>
                        prev.includes(time) ? prev : [...prev, time]
                    );
                }
                closeConcernDialog();
                refetchTodayConcerns();
            },
        }
    );

    const submitConcern = () => {
        if (!concernText.trim()) {
            setConcernError('Please enter a concern');
            return;
        }
        submitAttendanceConcern.mutate({
            concern_text: concernText.trim(),
            attendance_date:
                toIndiaCalendarDate(concernUpdate?.date) ||
                moment(concernUpdate?.date).format(SERVER_DATE_FORMAT),
            attendance_time: getUpdateConcernTime(concernUpdate) || null,
            attendance_message: concernUpdate?.message || '',
        });
    };

    const getEventTime = (item) => {
        if (item?.momentTime?.isValid?.()) return item.momentTime;
        const timeMatch = item.message?.match(/(\d{1,2}:\d{2})/);
        if (timeMatch && item.date) {
            const parsed = moment(
                `${toIndiaCalendarDate(item.date)} ${timeMatch[1]}`,
                "YYYY-MM-DD HH:mm"
            );
            if (parsed.isValid()) return parsed;
        }
        if (item?.time) {
            const parsed = moment(item.time, ["hh:mm a", "HH:mm:ss", "hh:mm:ss"], true);
            if (parsed.isValid()) return parsed;
        }
        return null;
    };

    const computeStats = (timelineData = []) => {
        let checkinTime = null;
        let mealCount = 0;
        let absentDetected = false;

        let totalNapSeconds = 0;
        let sleepStart = null;

        const statsDate = moment(selectedDate, SERVER_DATE_FORMAT);

        const sortedData = timelineData
            .filter(
                (item) =>
                    item.date &&
                    moment(toIndiaCalendarDate(item.date), SERVER_DATE_FORMAT).isSame(
                        statsDate,
                        "day"
                    )
            )
            .sort((a, b) => {
                const timeA = getEventTime(a);
                const timeB = getEventTime(b);
                if (timeA && timeB) return timeA.diff(timeB);
                return 0;
            });

        // Find the most recent Attendance entry
        const mostRecentAttendance = sortedData
            .filter(item => item.type === "Attendance")
            .slice(-1)[0]; // Get the last (most recent) Attendance entry

        // Process only the most recent Attendance entry
        if (mostRecentAttendance) {
            const { message, time } = mostRecentAttendance;
            const lowerMsg = message?.toLowerCase() || "";
            if (lowerMsg.includes("absent today")) absentDetected = true;
            else if (time) checkinTime = time;
        }

        sortedData.forEach((item) => {
            const { type, message } = item;
            const lowerMsg = message?.toLowerCase() || "";
            const eventTime = getEventTime(item);

            /* Meals */
            if (["Breakfast", "Lunch", "Snacks", "Fruits"].includes(type)) mealCount += 1;

            /* Sleep — use momentTime (seconds) so Slept/Woke in the same minute sort correctly */
            if (type === "Sleep" && eventTime) {
                if (lowerMsg.includes("slept")) {
                    sleepStart = eventTime;
                }
                if (lowerMsg.includes("woke") && sleepStart) {
                    let diffSeconds = eventTime.diff(sleepStart, "seconds");
                    if (diffSeconds < 0) diffSeconds += 24 * 60 * 60;
                    if (diffSeconds > 0) totalNapSeconds += diffSeconds;
                    sleepStart = null;
                }
            }
        });

        // Format total nap duration (sums every slept→woke pair for the day)
        const totalNapMinutes = Math.round(totalNapSeconds / 60);
        const hours = Math.floor(totalNapMinutes / 60);
        const minutes = totalNapMinutes % 60;
        const napDuration = totalNapMinutes > 0 ? (hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`) : "--:--";

        return {
            checkin: checkinTime || "--:--",
            naps: napDuration,
            meals: mealCount > 0 ? `${mealCount} served` : "0 served",
            isAbsent: absentDetected,
        };
    };







    // derive today's group and previous groups (memoized)
    const { todayGroup, previousGroups } = useMemo(() => {
        const tg = findTimelineGroupForDate(ChildTimeline, selectedDate);
        const prev = Array.isArray(ChildTimeline)
            ? ChildTimeline.filter((g) => g !== tg)
            : [];
        return { todayGroup: tg, previousGroups: prev };
    }, [ChildTimeline, selectedDate]);

    // whenever ChildTimeline or selectedDate changes, recompute stats for today's group
    useEffect(() => {
        const dataForStats = todayGroup?.data || [];
        const childStats = computeStats(dataForStats);
        setStats(childStats);
        setIsAbsent(childStats.isAbsent);
    }, [ChildTimeline, selectedDate, todayGroup]);

    // same logic as in ChildTimeline fetch
    const { isLoading, isFetching, refetch } = useQueryWrapper(
        ["ChildTimeline", "timeline" + activeChild?.id, childTimeline, selectedDate],
        () =>
            fetchChildTimeline(
                childTimeline.startIndex,
                setDataAvailable,
                ChildTimeline,
                ITEM_PER_PAGE,
                constants?.INCLUDE_TYPES,
                setLength,
                setTimeline,
                selectedDate
            ),
        { keepPreviousData: true },
        {
            onSuccess: (data) => {
                if (data?.status) {
                    console.error("Timeline fetch error", data?.error);
                } else {
                    setChildTimeline(data);
                    if (Array.isArray(data) && data[0]?.data) {
                        // do not assume data[0] is today — compute stats from the matching group instead
                        const matched = findTimelineGroupForDate(data, selectedDate);
                        const childStats = computeStats(matched?.data || []);
                        setStats(childStats);
                    } else {
                        console.warn("No valid timeline data for stats");
                    }
                }
            },
        }
    );

    const loadMore = () => {
        setChildTimeline((prev) => ({
            ...prev,
            startIndex: (prev.startIndex || 0) + ITEM_PER_PAGE,
        }));
    };

    // If absent for selected date show absent UI
    if (isAbsent) {
        return (
            <div className="p-4 space-y-4">
                {trialBanner}
                <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border-2 border-orange-300 rounded-3xl p-8 text-center">
                    <div className="text-6xl mb-4">🏠</div>
                    <h3 className="text-xl font-bold text-gray-800 mb-2">
                        {activeChild?.name} is absent today
                    </h3>
                    <p className="text-gray-600">We hope to see them soon!</p>
                </div>

                <div className="bg-gradient-to-br from-cyan-500 to-blue-600 rounded-3xl p-6">
                    <h3 className="text-white text-lg font-semibold mb-4">
                        Still want to stay connected?
                    </h3>
                    <div className="space-y-3">
                        <button
                            onClick={() => navigate('/chat')}
                            className="w-full bg-white text-cyan-600 py-3 rounded-2xl font-semibold flex items-center justify-center space-x-2"
                        >
                            <MessageCircle className="w-5 h-5" />
                            <span>Chat with Teacher</span>
                        </button>
                    </div>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-4">Previous Day Updates</h3>
                <div className="space-y-4">
                    {previousGroups && previousGroups.length > 0 ? (
                        previousGroups.map((dayGroup, dayIdx) => (
                            <div
                                key={dayIdx}
                                className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100"
                            >
                                <h3 className="font-bold text-gray-800 mb-3">
                                    {moment(dayGroup.dateGroup, ["DD-MM-YY", "DD-MM-YYYY"]).format("MMMM DD, YYYY")}
                                </h3>

                                {dayGroup.data && dayGroup.data.length > 0 ? (
                                    <div className="space-y-3">
                                        {dayGroup.data.map((update, idx) => (
                                            <UpdateCard
                                                key={idx}
                                                update={update}
                                                setSelectedPhoto={setSelectedPhoto}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="bg-gray-50 rounded-xl p-4 text-center">
                                        <p className="text-gray-600">
                                            No updates for this day
                                        </p>
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <div className="bg-gray-50 rounded-xl p-4 text-center">
                            <p className="text-gray-600">No previous day updates found</p>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="p-4 space-y-4">
            {isLoading && <SpinnerLoading />}
            {submitAttendanceConcern.isLoading && <SpinnerLoading />}
            {/* Welcome Banner */}
            <div className="bg-gradient-to-r from-cyan-500 to-blue-600 rounded-3xl p-6 flex items-center justify-between">
                <div className="text-white">
                    <p className="text-sm opacity-90 mb-1">Welcome back</p>
                    <h2 className="text-xl md:text-2xl font-bold mb-1">{activeChild?.name} {activeChild?.last_name}</h2>
                    <p className="text-sm opacity-90">{activeChild?.["room.name"]}</p>
                </div>
                <button
                    onClick={() => navigate('/cctv')}
                    className="bg-white text-cyan-600 px-6 py-3 rounded-2xl font-semibold flex items-center space-x-2 shadow-lg"
                >
                    <Video className="w-5 h-5" />
                    <span>Watch Live</span>
                </button>
            </div>

            {trialBanner}

            {/* Stats Cards */}
            <div className="grid grid-cols-3 gap-3">
                <StatsCard
                    icon={CheckCircle}
                    label={textOrDefault(constants?.statsCheckInLabel, 'Check-in / Check-out')}
                    value={stats?.checkin}
                    gradient="from-blue-400 to-blue-600"
                    bgGradient="from-blue-50 to-blue-100"
                    borderColor="border-blue-200"
                    textColor="text-blue-600"
                    onClick={() => navigate('/checkin-history')}
                />
                <StatsCard
                    icon={Moon}
                    label={textOrDefault(constants?.statsNapsLabel, 'Naps')}
                    value={stats?.naps}
                    gradient="from-purple-400 to-purple-600"
                    bgGradient="from-purple-50 to-purple-100"
                    borderColor="border-purple-200"
                    textColor="text-purple-600"
                    onClick={() => document.getElementById('nap-section')?.scrollIntoView({ behavior: 'smooth' })}
                />
                <StatsCard
                    icon={Utensils}
                    label={textOrDefault(constants?.statsMealLabel, 'Meal')}
                    value={stats?.meals}
                    gradient="from-orange-400 to-orange-600"
                    bgGradient="from-orange-50 to-orange-100"
                    borderColor="border-orange-200"
                    textColor="text-orange-600"
                    onClick={() =>
                        navigate('/schedule', {
                            state: { activeTab: 'menu' }
                        })
                    }
                />
            </div>

            {/* Announcement Banner */}
            {/* <AnnouncementBanner
                childName={activeChild?.name}
            /> */}

            <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-gray-800">Today's Updates</h3>
                </div>

                <div className="space-y-4">
                    {todayGroup && Array.isArray(todayGroup.data) && todayGroup.data.length > 0 ? (
                        todayGroup.data.map((update, idx) => (
                            <UpdateCard
                                key={idx}
                                update={update}
                                setSelectedPhoto={setSelectedPhoto}
                                showRaiseConcern={showRaiseConcern}
                                hasConcern={todayConcernTimes.has(getUpdateConcernTime(update))}
                                onRaiseConcern={openConcernDialog}
                            />
                        ))
                    ) : (
                        <div className="bg-gray-50 rounded-xl p-4 text-center">
                            <p className="text-gray-600">No updates for today</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Previous Days Updates (toggle) */}
            {showMoreUpdates && (
                <div className="space-y-4">
                    {previousGroups && previousGroups.length > 0 ? (
                        previousGroups.map((dayGroup, dayIdx) => (
                            <div
                                key={dayIdx}
                                className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100"
                            >
                                <h3 className="font-bold text-gray-800 mb-3">
                                    {moment(dayGroup.dateGroup, ["DD-MM-YY", "DD-MM-YYYY"]).format("MMMM DD, YYYY")}
                                </h3>

                                {dayGroup.data && dayGroup.data.length > 0 ? (
                                    <div className="space-y-3">
                                        {dayGroup.data.map((update, idx) => (
                                            <UpdateCard
                                                key={idx}
                                                update={update}
                                                setSelectedPhoto={setSelectedPhoto}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <div className="bg-gray-50 rounded-xl p-4 text-center">
                                        <p className="text-gray-600">
                                            No updates for this day
                                        </p>
                                    </div>
                                )}
                            </div>
                        ))
                    ) : (
                        <div className="bg-gray-50 rounded-xl p-4 text-center">
                            <p className="text-gray-600">No previous day updates found</p>
                        </div>
                    )}
                </div>
            )}

            <button
                onClick={() => setShowMoreUpdates(!showMoreUpdates)}
                className="w-full bg-gray-100 text-gray-700 py-3 rounded-xl font-semibold hover:bg-gray-200 transition"
            >
                {showMoreUpdates ? 'Show Less' : 'Load Previous Days'}
            </button>

            {concernUpdate && (
                <div
                    className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
                    onClick={closeConcernDialog}
                >
                    <div
                        className="bg-white w-full max-w-md rounded-2xl shadow-xl overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="bg-cyan-500 p-4 flex justify-between items-center rounded-t-2xl">
                            <h3 className="text-white font-semibold text-lg">Raise a Concern</h3>
                            <button type="button" onClick={closeConcernDialog} aria-label="Close">
                                <X className="w-6 h-6 text-white" />
                            </button>
                        </div>

                        <div className="p-4 space-y-4">
                            <div>
                                <textarea
                                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-cyan-500 h-28 outline-none focus:border-transparent"
                                    placeholder="Enter your concern"
                                    value={concernText}
                                    onChange={(e) => {
                                        setConcernText(e.target.value);
                                        setConcernError('');
                                    }}
                                />
                                {concernError && (
                                    <p className="text-red-500 text-sm mt-1">{concernError}</p>
                                )}
                            </div>

                            <button
                                type="button"
                                onClick={submitConcern}
                                disabled={submitAttendanceConcern.isLoading}
                                className="w-full bg-cyan-500 text-white py-3 rounded-xl font-semibold hover:bg-cyan-600 transition disabled:opacity-60"
                            >
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Home;
